/**
 * Invite-accept page, shown by the cloud shell on `/invite/accept?token=…`
 * (the link Bliksys emails). The app forces cloud mode on this path so it
 * works in dev too — see App in main.tsx.
 *
 * Flow:
 *   - Load a preview of the invitation (org + role).
 *   - Signed in with the matching email → one-click accept, then land in
 *     the org.
 *   - Signed in as someone else → ask them to sign out.
 *   - Signed out → embed the normal sign-in (with the invited email
 *     pre-filled). Password/OAuth sign-in resolves in place; magic-link
 *     carries a returnTo back to this page (so dev works), after which the
 *     accept button appears.
 */

import { useEffect, useState } from 'react';

import { useAuth } from '../auth/AuthContext.js';
import { inviteApi, type InvitePreview } from '../data/inviteApi.js';
import { navigateToLanding } from '../router.js';

import { LoadingScreen } from './LoadingScreen.js';
import { SignIn } from './SignIn.js';
import { StatusScreen } from './StatusScreen.js';
import './SignIn.css';

export function InviteAccept({ token }: { token: string | null }): JSX.Element {
  const auth = useAuth();
  // undefined = loading, null = invalid/expired, object = found.
  const [invite, setInvite] = useState<InvitePreview | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setInvite(null);
      return;
    }
    let cancelled = false;
    void inviteApi()
      .preview(token)
      .then((p) => {
        if (!cancelled) setInvite(p);
      })
      .catch(() => {
        if (!cancelled) setInvite(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async (): Promise<void> => {
    if (!token || !invite) return;
    setBusy(true);
    setError(null);
    try {
      await inviteApi().acceptAsMe(token);
      // Land the user in the org they just joined.
      await auth.refresh();
      await auth.switchOrg(invite.orgSlug);
      navigateToLanding();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not accept the invitation.');
      setBusy(false);
    }
  };

  if (invite === undefined) {
    return <LoadingScreen message="Loading invitation…" />;
  }
  if (invite === null) {
    return (
      <StatusScreen
        tone="error"
        title="Invitation not found"
        message="This invitation is invalid or has expired. Ask whoever invited you to send a new one."
      >
        <a className="signin-submit" href="/?cloud" style={{ textDecoration: 'none' }}>
          Go to Bliksys
        </a>
      </StatusScreen>
    );
  }

  // Signed out → sign in (email pre-filled), then this page re-renders.
  if (auth.status !== 'authenticated' || !auth.user) {
    return (
      <SignIn
        initialEmail={invite.email}
        heading={`Join ${invite.orgName}`}
        subheading={`You've been invited as ${invite.orgRole}. Sign in to accept.`}
      />
    );
  }

  // Signed in as the wrong account.
  if (auth.user.email.toLowerCase() !== invite.email.toLowerCase()) {
    return (
      <StatusScreen
        tone="warning"
        title="Wrong account"
        message={
          <>
            This invitation is for <strong>{invite.email}</strong>, but you&apos;re signed in as{' '}
            <strong>{auth.user.email}</strong>. Sign out and accept with the invited email.
          </>
        }
      >
        <button type="button" className="signin-submit" onClick={() => void auth.signOut()}>
          Sign out
        </button>
      </StatusScreen>
    );
  }

  // Signed in with the right email → accept.
  return (
    <StatusScreen
      tone="info"
      title={`Join ${invite.orgName}`}
      message={
        <>
          You&apos;ve been invited to <strong>{invite.orgName}</strong> as{' '}
          <strong>{invite.orgRole}</strong>.
        </>
      }
    >
      {error && (
        <p className="signin-error" role="alert">
          {error}
        </p>
      )}
      <button type="button" className="signin-submit" disabled={busy} onClick={() => void accept()}>
        {busy ? 'Accepting…' : 'Accept invitation'}
      </button>
    </StatusScreen>
  );
}
